import React, { useEffect, useRef, useState } from 'react';
import { TeamRegistrationService } from '../TeamRegistrationService';

interface TotalPriceProps {
    amount: number;
    className?: string;
    durationMs?: number;
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

const TotalPrice: React.FC<TotalPriceProps> = ({ amount, className, durationMs = 500 }) => {
    const [displayAmount, setDisplayAmount] = useState<number>(amount);
    const previousAmountRef = useRef<number>(amount);
    const [animationKey, setAnimationKey] = useState<number>(0);
    const rafRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(0);
    const startValueRef = useRef<number>(amount);
    const latestTargetRef = useRef<number>(amount);
    const displayAmountRef = useRef<number>(amount);

    // Keep a live ref of the displayed value to start animations from the latest shown number
    useEffect(() => {
        displayAmountRef.current = displayAmount;
    }, [displayAmount]);

    useEffect(() => {
        // Skip if amount unchanged
        if (amount === latestTargetRef.current) return;

        // Cancel any ongoing animation
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }

        // Initialize animation state
        startTimeRef.current = performance.now();
        startValueRef.current = displayAmountRef.current;
        // store previous target before switching to new target
        previousAmountRef.current = latestTargetRef.current;
        latestTargetRef.current = amount;

        const step = (now: number) => {
            const elapsedMs = now - startTimeRef.current;
            const rawProgress = durationMs <= 0 ? 1 : Math.min(1, elapsedMs / durationMs);
            const eased = easeOutCubic(rawProgress);
            const current = startValueRef.current + (latestTargetRef.current - startValueRef.current) * eased;
            setDisplayAmount(current);
            if (rawProgress < 1) {
                rafRef.current = requestAnimationFrame(step);
            } else {
                setDisplayAmount(latestTargetRef.current);
                rafRef.current = null;
            }
        };

        rafRef.current = requestAnimationFrame(step);

        // Bump key to re-mount the animated span and retrigger CSS animation
        setAnimationKey((prev) => prev + 1);

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [amount, durationMs]);

    // Cleanup on unmount
    useEffect(() => () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }, []);

    const rounded = Math.round(displayAmount * 100) / 100;

    const isIncrease = rounded >= (previousAmountRef.current ?? rounded);
    const composedClassName = `${className ?? ''} price-change price-change-animate`.trim();
    const cssDurationVar = {
        ['--price-change-duration' as any]: `${durationMs}ms`,
        ['--price-glow-color' as any]: isIncrease ? 'rgba(16, 185, 129, 0)' : 'rgba(239, 68, 68, 0)',
        ['--price-glow-strong' as any]: isIncrease ? 'rgba(16, 185, 129, 0.55)' : 'rgba(239, 68, 68, 0.6)',
        ['--price-glow-soft' as any]: isIncrease ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.3)'
    } as React.CSSProperties;

    return (
        <span key={animationKey} className={composedClassName} style={cssDurationVar}>
            {TeamRegistrationService.formatCurrencyDE(rounded)}
        </span>
    );
};

export default TotalPrice;


